import { useEffect } from 'react';

interface SEOHeadProps {
  title: string;
  description: string;
  canonical?: string;
  keywords?: string;
  ogTitle?: string;
  ogDescription?: string;
  ogImage?: string;
  ogType?: string;
  twitterCard?: string;
  schema?: object | object[];
  noindex?: boolean;
}

export default function SEOHead({
  title,
  description,
  canonical,
  keywords,
  ogTitle,
  ogDescription,
  ogImage,
  ogType = 'website',
  twitterCard = 'summary_large_image',
  schema,
  noindex = false,
}: SEOHeadProps) {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://jackspumps.ie';
  const fullTitle = title;
  const fullCanonical = canonical 
    ? (canonical.startsWith('http') ? canonical : `${siteUrl}${canonical}`)
    : `${siteUrl}${window.location.pathname}`;
  const fullOgTitle = ogTitle || title;
  const fullOgDescription = ogDescription || description;
  const fullOgImage = ogImage || `${siteUrl}/og-image.jpg`;

  useEffect(() => {
    // Set title
    document.title = fullTitle;

    // Helper function to set or update meta tags
    const setMetaTag = (property: string, content: string, isProperty = false) => {
      const attribute = isProperty ? 'property' : 'name';
      let element = document.querySelector(`meta[${attribute}="${property}"]`);
      
      if (!element) {
        element = document.createElement('meta');
        element.setAttribute(attribute, property);
        document.head.appendChild(element);
      }
      
      element.setAttribute('content', content);
    };

    // Helper function to set or update link tags
    const setLinkTag = (rel: string, href: string) => {
      let element = document.querySelector(`link[rel="${rel}"]`) as HTMLLinkElement;
      
      if (!element) {
        element = document.createElement('link');
        element.setAttribute('rel', rel);
        document.head.appendChild(element);
      }
      
      element.setAttribute('href', href);
    };

    // Set basic meta tags
    setMetaTag('description', description);
    if (keywords) {
      setMetaTag('keywords', keywords);
    }
    
    // Set canonical
    setLinkTag('canonical', fullCanonical);
    
    // Set robots
    if (noindex) {
      setMetaTag('robots', 'noindex, nofollow');
    } else {
      // Remove noindex if it exists
      const robotsMeta = document.querySelector('meta[name="robots"]');
      if (robotsMeta && robotsMeta.getAttribute('content') === 'noindex, nofollow') {
        robotsMeta.remove();
      }
    }
    
    // Set Open Graph tags
    setMetaTag('og:title', fullOgTitle, true);
    setMetaTag('og:description', fullOgDescription, true);
    setMetaTag('og:type', ogType, true);
    setMetaTag('og:url', fullCanonical, true);
    setMetaTag('og:image', fullOgImage, true);
    setMetaTag('og:site_name', "Jack's Pumps", true);
    
    // Set Twitter Card tags
    setMetaTag('twitter:card', twitterCard);
    setMetaTag('twitter:title', fullOgTitle);
    setMetaTag('twitter:description', fullOgDescription);
    setMetaTag('twitter:image', fullOgImage);
    
    // Set structured data — use a dedicated tag so we don't overwrite global schemas in index.html
    if (schema) {
      let scriptElement = document.querySelector('script[data-page-schema="true"]') as HTMLScriptElement | null;

      if (!scriptElement) {
        scriptElement = document.createElement('script');
        scriptElement.setAttribute('type', 'application/ld+json');
        scriptElement.setAttribute('data-page-schema', 'true');
        document.head.appendChild(scriptElement);
      }

      scriptElement.textContent = JSON.stringify(Array.isArray(schema) ? schema : [schema]);
    }
  }, [title, description, fullCanonical, keywords, fullOgTitle, fullOgDescription, fullOgImage, ogType, twitterCard, schema, noindex]);

  return null;
}